import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { request } from './client'
import {
  screenshotResultsSignature,
  screenshotsPollInterval,
  type InstagramScreenshot,
  type InstagramScreenshots,
  type PendingInstagramScreenshots,
  type ScreenshotKind,
} from '../lib/instagramScreenshots'

const ROOT = '/api/analytics/instagram'
export const screenshotKeys = {
  reel: (shortcode: string) => ['instagram-screenshots', shortcode] as const,
  pending: ['instagram-screenshot-pending'] as const,
}
const reelPath = (shortcode: string) => `${ROOT}/reels/${encodeURIComponent(shortcode)}/screenshots`

export function refreshScreenshotResults(cache: QueryClient, shortcode: string): void {
  void cache.invalidateQueries({ queryKey: screenshotKeys.pending })
  // Артефакты с подписанным video_url не трогаем: новый график не должен перезагрузить плеер.
  void cache.invalidateQueries({
    queryKey: ['instagram-analytics'],
    predicate: (query) => query.queryKey[1] === 'reel'
      ? query.queryKey[2] === shortcode
      : ['catalog', 'content', 'overview', 'hypothesis'].includes(String(query.queryKey[1])),
  })
}

export function useInstagramScreenshots(shortcode: string) {
  const cache = useQueryClient()
  const query = useQuery({
    queryKey: screenshotKeys.reel(shortcode),
    queryFn: ({ signal }) => request<InstagramScreenshots>(reelPath(shortcode), { signal }),
    refetchInterval: (query) => screenshotsPollInterval(query.state.data),
    refetchIntervalInBackground: false,
    staleTime: 0,
  })
  const previous = useRef<string | null>(null)
  const signature = screenshotResultsSignature(query.data)
  useEffect(() => {
    if (signature && signature !== previous.current) refreshScreenshotResults(cache, shortcode)
    previous.current = signature
  }, [signature, cache, shortcode])
  return query
}

export function usePendingInstagramScreenshots(enabled = true) {
  return useQuery({
    queryKey: screenshotKeys.pending,
    queryFn: ({ signal }) => request<PendingInstagramScreenshots>(`${ROOT}/screenshots/pending`, { signal }),
    enabled,
    staleTime: 10_000,
    refetchInterval: enabled ? 30_000 : false,
    refetchIntervalInBackground: false,
  })
}

type ScreenshotChange = {
  kind: ScreenshotKind
  file?: File
  action?: 'retry' | 'cancel'
  revision?: string | null
}

export function useChangeInstagramScreenshot(shortcode: string) {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: ({ kind, file, action, revision }: ScreenshotChange) => {
      const path = `${reelPath(shortcode)}/${kind}`
      if (file) {
        const body = new FormData()
        body.append('file', file)
        return request<InstagramScreenshot>(path, { method: 'POST', body })
      }
      const suffix = revision ? `?revision=${encodeURIComponent(revision)}` : ''
      return request<InstagramScreenshot>(`${path}/${action}${suffix}`, { method: 'POST' })
    },
    onSuccess: (item) => {
      cache.setQueryData<InstagramScreenshots>(screenshotKeys.reel(shortcode), (previous) => previous ? {
        ...previous,
        items: [...previous.items.filter((value) => value.kind !== item.kind), item],
      } : undefined)
      if (item.status === 'ready') refreshScreenshotResults(cache, shortcode)
      else void cache.invalidateQueries({ queryKey: screenshotKeys.pending })
    },
    onSettled: () => { void cache.invalidateQueries({ queryKey: screenshotKeys.reel(shortcode) }) },
  })
}
